import { useCallback } from 'react';
import { useNavigate } from 'react-router';

/**
 * Back navigation for detail pages that are reachable from more than one list.
 *
 * Going back through the history keeps whatever state the previous page held (list page, filters,
 * scroll). When there is nothing to go back to — a shared link, a refresh, a new tab — it would
 * take the user out of the app, so we send them to `fallbackUrl` instead.
 *
 * `history.state.idx` is the index react-router keeps for the current entry inside the session's
 * history stack: 0 means this page is the first one. `location.key !== 'default'` cannot be used
 * here because a `navigate(..., { replace: true })` on mount (the auth/public route sync) gives the
 * first entry a real key while leaving the stack empty.
 */
export const useGoBack = (fallbackUrl: string) => {
  const navigate = useNavigate();

  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;

    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
      return;
    }

    navigate(fallbackUrl, { replace: true });
  }, [navigate, fallbackUrl]);
};
